import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/CardsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the cards list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['cards', customerId, currentToken]` — a bare string plus a page token,
// which makes the cache page-shaped rather than list-shaped: every page landed
// under its own key, so invalidating "the list" meant invalidating one page.
//
// ⚠️ Every key below is scoped by `customerId`. Cards belong to a customer —
// `listCards` REQUIRES the filter — so two customers' lists must never share a
// cache entry. Dropping customerId from the key would serve customer A's cards
// to customer B, which is a data-exposure bug rather than a staleness one.
export const cardsKeys = {
    all: ['cards'] as const,
    lists: () => [...cardsKeys.all, 'list'] as const,
    list: (customerId: string, filters: Record<string, string>) =>
        [...cardsKeys.lists(), customerId, {filters}] as const,
    details: () => [...cardsKeys.all, 'detail'] as const,
    detail: (customerId: string, id: string) =>
        [...cardsKeys.details(), customerId, id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which read
 * as compliant (a query hook, a page control, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe.
 * `useInfiniteQuery` concatenates pages instead, so the client holds a growing
 * loaded window and the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever. `?? undefined` is doing
 * real work: the service returns `nextToken?: string`, so the absent case
 * arrives as `undefined` already, but a GraphQL `null` would slip through.
 *
 * Every filter is in the queryKey (§9): omit one and changing it silently
 * serves the previous filter's cache.
 */
export const useCardsInfiniteQuery = (
    customerId: string,
    filters: Record<string, string> = {},
) => {
    return useInfiniteQuery({
        queryKey: cardsKeys.list(customerId, filters),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords(customerId, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
        // The list is meaningless without a customer, and `fetchRecords` would
        // send `filter: {customerId: undefined}` — which the API answers with
        // every card it can see. Gate rather than let that request leave.
        enabled: !!customerId,
    });
};

/**
 * Batch delete. The list key is invalidated on success so the table reflects
 * the removal without a manual reload (§8).
 */
export const useDeleteCardsMutation = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(customerId, id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Card deleted'
                    : `${ids.length} cards deleted`,
            );
            queryClient.invalidateQueries({queryKey: cardsKeys.all});
        },
        onError: notifyError,
    });
};
